/**
 * VertriebsgebietOverlayContent Component
 * Displays detailed Vertriebsgebiet (Sales Region) data in the overlay
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Users, Baby, Home, Key, Users2, FileText } from 'lucide-react';
import mapGermany from '@/assets/map_germany.png';
import { AnalysisChart } from '@/components/charts/AnalysisChart/AnalysisChart';
import type { VertriebsgebietFullData } from '@/types/vertriebsgebiet';
import type { ChartConfig } from '@/types/api';
import { SectionDescriptionText } from '@/components/results/SectionDescriptionText';
import type { GetDescriptionFn } from '@/types/sectionDescription';

export interface VertriebsgebietOverlayContentProps {
  data: VertriebsgebietFullData;
  getDescription?: GetDescriptionFn;
  featureName?: string;
}

/**
 * Formats a number with German locale (dot as thousands separator)
 * Returns '-' for zero or undefined values
 */
function formatNumber(value: number | undefined): string {
  if (value === undefined || value === 0) return '-';
  return value.toLocaleString('de-DE');
}

/**
 * Formats currency value with Euro symbol
 * Returns '-' for zero or undefined values
 */
function formatCurrency(value: number | undefined): string {
  if (value === undefined || value === 0) return '-';
  return `${value.toLocaleString('de-DE')} €`;
}

/**
 * Checks if a string value is available (not empty, not "not available")
 */
function isValueAvailable(value: string | undefined): boolean {
  if (!value) return false;
  const lowerValue = value.toLowerCase();
  return lowerValue !== 'not available' && lowerValue !== 'n/a' && value.trim() !== '';
}

/**
 * Formats decimal number with German locale
 * Returns '-' for zero or undefined values
 */
function formatDecimal(value: number | undefined): string {
  if (value === undefined || value === 0) return '-';
  return value.toLocaleString('de-DE', { minimumFractionDigits: 2 });
}

/**
 * Overlay content for Vertriebsgebiet with full details including milieu descriptions
 */
// Color palette for the donut chart - based on eclou.net brand gradient (pink #FC03B0 to orange #FF6803)
const MILIEU_COLORS = [
  '#FC03B0', // Primary pink (brand start)
  '#FF6803', // Primary orange (brand end)
  '#E8148A', // Darker pink
  '#FF8533', // Lighter orange
  '#D02090', // Deep magenta
  '#FFB366', // Soft orange
  '#A01870', // Muted magenta
  '#CC5200', // Deep orange
];

export function VertriebsgebietOverlayContent({
  data,
  getDescription,
  featureName,
}: VertriebsgebietOverlayContentProps) {
  const { t } = useTranslation();
  const { main, milieus } = data;

  // Get top 3 milieus from main data for card display, filtering out unavailable ones
  const topMilieus = [
    { name: main.Vertriebsgebiet_Top_1_Millieu_Name, percent: main.Vertriebsgebiet_Top_1_Millieu_Prozent },
    { name: main.Vertriebsgebiet_Top_2_Millieu_Name, percent: main.Vertriebsgebiet_Top_2_Millieu_Prozent },
    { name: main.Vertriebsgebiet_Top_3_Millieu_Name, percent: main.Vertriebsgebiet_Top_3_Millieu_Prozent },
  ].filter(m => isValueAvailable(m.name) && m.percent > 0);

  // Match milieu descriptions and relevance with top milieus
  const milieusWithDescriptions = topMilieus.map((topMilieu) => {
    const details = milieus.find(
      (m) => m.milieu_name.toLowerCase() === topMilieu.name.toLowerCase()
    );
    return {
      ...topMilieu,
      description: details?.milieu_description || '',
      relevance: details?.milieu_relevance || '',
    };
  });

  // Check if there's any milieu data to show
  const hasMilieuData = topMilieus.length > 0 || milieus.length > 0;

  // Create dynamic donut chart config based on milieus array
  const donutChartConfig = useMemo((): ChartConfig | null => {
    // Use milieus array if available, otherwise fall back to topMilieus
    const chartData = milieus.length > 0
      ? milieus.map(m => ({ name: m.milieu_name, percent: m.milieu_percent }))
      : topMilieus;

    // Return null if no data available
    if (chartData.length === 0) {
      return null;
    }

    return {
      type: 'donut',
      series: chartData.map(m => m.percent) as unknown as ChartConfig['series'],
      options: {
        labels: chartData.map(m => m.name),
        colors: MILIEU_COLORS.slice(0, chartData.length),
        legend: {
          show: true,
          position: 'bottom',
        },
        dataLabels: {
          enabled: true,
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '14px',
                  fontWeight: 600,
                },
                value: {
                  show: true,
                  fontSize: '16px',
                  fontWeight: 700,
                  formatter: (val: string) => `${val}%`,
                },
                total: {
                  show: false,
                },
              },
            },
          },
        },
        tooltip: {
          y: {
            formatter: (val: number) => `${val}%`,
          },
        },
      } as ChartConfig['options'],
    };
  }, [milieus, topMilieus]);

  // Donut chart config for Haushaltsverteilung (household distribution)
  const haushaltsverteilungChartConfig = useMemo((): ChartConfig | null => {
    if (!main.Vertriebsgebiet_Haushaltverteilung || main.Vertriebsgebiet_Haushaltverteilung.length === 0) {
      return null;
    }

    return {
      type: 'donut',
      series: main.Vertriebsgebiet_Haushaltverteilung.map(h => h.Vertriebsgebiet_Haushaltverteilung_Prozent) as unknown as ChartConfig['series'],
      options: {
        labels: main.Vertriebsgebiet_Haushaltverteilung.map(h => h.Vertriebsgebiet_Haushaltverteilung_Name),
        colors: MILIEU_COLORS.slice(0, main.Vertriebsgebiet_Haushaltverteilung.length),
        legend: {
          show: true,
          position: 'bottom',
        },
        dataLabels: {
          enabled: true,
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '12px',
                  fontWeight: 600,
                },
                value: {
                  show: true,
                  fontSize: '14px',
                  fontWeight: 700,
                  formatter: (val: string) => `${val}%`,
                },
              },
            },
          },
        },
        tooltip: {
          y: {
            formatter: (val: number) => `${val}%`,
          },
        },
      } as ChartConfig['options'],
    };
  }, [main.Vertriebsgebiet_Haushaltverteilung]);

  // Bar chart config for Gebäudeart (building types)
  const gebaudeartChartConfig = useMemo((): ChartConfig | null => {
    if (!main.Vertriebsgebiet_Gebaudeart || main.Vertriebsgebiet_Gebaudeart.length === 0) {
      return null;
    }

    return {
      type: 'bar',
      series: [{
        name: t('results.cards.vertriebsgebiet.overlay.share'),
        data: main.Vertriebsgebiet_Gebaudeart.map(g => g.Vertriebsgebiet_Gebaudeart_Prozent),
      }] as unknown as ChartConfig['series'],
      options: {
        chart: {
          type: 'bar',
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: '60%',
          },
        },
        colors: ['#FC03B0'],
        xaxis: {
          categories: main.Vertriebsgebiet_Gebaudeart.map(g => g.Vertriebsgebiet_Gebaudeart_Name),
          labels: {
            style: {
              fontSize: '11px',
            },
          },
        },
        yaxis: {
          labels: {
            formatter: (val: number) => `${val}%`,
          },
        },
        dataLabels: {
          enabled: true,
          formatter: (val: number) => `${val}%`,
        },
        tooltip: {
          y: {
            formatter: (val: number) => `${val}%`,
          },
        },
      } as ChartConfig['options'],
    };
  }, [main.Vertriebsgebiet_Gebaudeart, t]);

  // Bar chart config for Kinder pro Haushalt (children per household)
  const kinderChartConfig = useMemo((): ChartConfig | null => {
    if (!main.Vertriebsgebiet_Haushalt_Kinder || main.Vertriebsgebiet_Haushalt_Kinder.length === 0) {
      return null;
    }

    return {
      type: 'bar',
      series: [{
        name: t('results.cards.vertriebsgebiet.overlay.share'),
        data: main.Vertriebsgebiet_Haushalt_Kinder.map(k => k.Vertriebsgebiet_Haushalt_Kinder_Prozent),
      }] as unknown as ChartConfig['series'],
      options: {
        chart: {
          type: 'bar',
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: '60%',
          },
        },
        colors: ['#FF6803'],
        xaxis: {
          categories: main.Vertriebsgebiet_Haushalt_Kinder.map(k => k.Vertriebsgebiet_Haushalt_Kinder_Name),
          labels: {
            style: {
              fontSize: '11px',
            },
          },
        },
        yaxis: {
          labels: {
            formatter: (val: number) => `${val}%`,
          },
        },
        dataLabels: {
          enabled: true,
          formatter: (val: number) => `${val}%`,
        },
        tooltip: {
          y: {
            formatter: (val: number) => `${val}%`,
          },
        },
      } as ChartConfig['options'],
    };
  }, [main.Vertriebsgebiet_Haushalt_Kinder, t]);

  return (
    <div className="space-y-6">
      {/* Header with icon and title - matches card header positioning */}
      <div className="hidden md:flex items-center gap-2 md:gap-3 pb-1 md:pb-2">
        <div className="p-2 md:p-2.5 rounded-lg bg-gradient-to-br from-[var(--color-primary-start)]/10 to-[var(--color-primary-end)]/10">
          <MapPin className="h-5 w-5 md:h-6 md:w-6 text-[var(--color-primary-start)]" />
        </div>
        <h3 className="text-base md:text-lg font-semibold leading-none tracking-tight text-[var(--color-charcoal)]">{t('results.cards.vertriebsgebiet.title')}</h3>
      </div>

      {/* Stats section with map - 2 columns on desktop, stacked on mobile */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left side - Key stats */}
        <div>
          <div className="flex justify-between items-center py-1.5 border-b border-[var(--glass-border)]">
            <span className="text-sm text-[var(--color-charcoal)]/60">{t('results.cards.vertriebsgebiet.overlay.plz')}</span>
            <span className="font-bold text-[var(--color-charcoal)]">{main.Vertriebsgebiet_PLZ}</span>
          </div>
          <div className="flex justify-between items-center py-1.5 border-b border-[var(--glass-border)]">
            <span className="text-sm text-[var(--color-charcoal)]/60">{t('results.cards.vertriebsgebiet.overlay.population')}</span>
            <span className="font-bold text-[var(--color-charcoal)]">
              {formatNumber(main.Vertriebsgebiet_Anzahl_Einwohner)}
            </span>
          </div>
          <div className="flex justify-between items-center py-1.5 border-b border-[var(--glass-border)]">
            <span className="text-sm text-[var(--color-charcoal)]/60">{t('results.cards.vertriebsgebiet.overlay.numberOfHouseholds')}</span>
            <span className="font-bold text-[var(--color-charcoal)]">{formatNumber(main.Vertriebsgebiet_Anzahl_Haushalte)}</span>
          </div>
          <div className="flex justify-between items-center py-1.5 border-b border-[var(--glass-border)]">
            <span className="text-sm text-[var(--color-charcoal)]/60">{t('results.cards.vertriebsgebiet.overlay.averageHouseholdSize')}</span>
            <span className="font-bold text-[var(--color-charcoal)]">
              {formatDecimal(main.Vertriebsgebiet_Durchschnittliche_Haushaltsgroesse)}
            </span>
          </div>
          <div className="flex justify-between items-center py-1.5 border-b border-[var(--glass-border)]">
            <span className="text-sm text-[var(--color-charcoal)]/60">{t('results.cards.vertriebsgebiet.overlay.purchasingPowerPerCapita')}</span>
            <span className="font-bold text-[var(--color-charcoal)]">{formatCurrency(main.Vertriebsgebiet_Kaufkraft_Pro_Einwohner)}</span>
          </div>
        </div>

        {/* Right side - Map (hidden on mobile) */}
        <div className="hidden md:flex items-start justify-center">
          <img
            src={mapGermany}
            alt={t('results.cards.vertriebsgebiet.overlay.mapAlt')}
            className="w-56 h-56 object-contain"
          />
        </div>
      </div>

      {/* Milieus section with donut chart and descriptions - only show if we have data */}
      {hasMilieuData && donutChartConfig && (
        <div className="pt-4 border-t border-[var(--glass-border)]">
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-lg font-semibold text-[var(--color-charcoal)]">{t('results.cards.vertriebsgebiet.overlay.topMilieusTitle')}</h4>
            <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="topMilieus" />
          </div>

          <div className="flex flex-col md:flex-row gap-4 md:gap-6">
            {/* Donut Chart */}
            <div className="flex-shrink-0 md:w-96">
              <AnalysisChart
                config={donutChartConfig}
                height={340}
                className="bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)] p-4"
              />
            </div>

            {/* Milieu details - below chart on mobile */}
            {milieusWithDescriptions.length > 0 && (
              <div className="flex-1 space-y-3">
                {milieusWithDescriptions.map((milieu, index) => (
                  <div
                    key={index}
                    className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]"
                  >
                    <div className="flex items-start gap-3">
                      {/* Ranking badge */}
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-[var(--color-primary-start)] to-[var(--color-primary-end)] flex items-center justify-center">
                        <span className="text-white font-bold">{index + 1}</span>
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <div className="mb-1">
                          <h5 className="font-semibold text-[var(--color-charcoal)]">
                            {milieu.name}
                          </h5>
                        </div>
                        {milieu.description && (
                          <p className="text-sm text-[var(--color-charcoal)]/70 leading-relaxed">
                            {milieu.description}
                          </p>
                        )}
                        {milieu.relevance && (
                          <p className="text-sm text-[var(--color-primary-start)] leading-relaxed mt-2">
                            <span className="font-medium">{t('results.cards.vertriebsgebiet.overlay.relevance')}</span> {milieu.relevance}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Demographics section */}
      {(main.Vertriebsgebiet_Einwohner_Prozent_Maenner !== undefined ||
        main.Vertriebsgebiet_Einwohner_Prozent_Frauen !== undefined ||
        main.Vertriebsgebiet_Einwohner_Prozent_Kinder !== undefined) && (
        <div className="pt-4 border-t border-[var(--glass-border)]">
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-lg font-semibold text-[var(--color-charcoal)]">{t('results.cards.vertriebsgebiet.overlay.demographicsTitle')}</h4>
            <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="demographics" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Gender distribution */}
            {(main.Vertriebsgebiet_Einwohner_Prozent_Maenner !== undefined &&
              main.Vertriebsgebiet_Einwohner_Prozent_Frauen !== undefined) && (
              <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="h-5 w-5 text-[var(--color-primary-start)]" />
                  <span className="text-sm font-medium text-[var(--color-charcoal)]">{t('results.cards.vertriebsgebiet.overlay.genderDistribution')}</span>
                </div>

                {/* Stacked bar for gender */}
                <div className="h-8 rounded-full overflow-hidden flex mb-3">
                  <div
                    className="h-full flex items-center justify-center text-xs font-semibold text-white bg-[var(--color-primary-end)]"
                    style={{ width: `${main.Vertriebsgebiet_Einwohner_Prozent_Maenner}%` }}
                  >
                    {main.Vertriebsgebiet_Einwohner_Prozent_Maenner}%
                  </div>
                  <div
                    className="h-full flex items-center justify-center text-xs font-semibold text-white bg-[var(--color-primary-start)]"
                    style={{ width: `${main.Vertriebsgebiet_Einwohner_Prozent_Frauen}%` }}
                  >
                    {main.Vertriebsgebiet_Einwohner_Prozent_Frauen}%
                  </div>
                </div>

                {/* Legend */}
                <div className="flex justify-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[var(--color-primary-end)]" />
                    <span className="text-[var(--color-charcoal)]/70">{t('results.cards.vertriebsgebiet.overlay.male')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[var(--color-primary-start)]" />
                    <span className="text-[var(--color-charcoal)]/70">{t('results.cards.vertriebsgebiet.overlay.female')}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Children percentage */}
            {main.Vertriebsgebiet_Einwohner_Prozent_Kinder !== undefined && (
              <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
                <div className="flex items-center gap-2 mb-3">
                  <Baby className="h-5 w-5 text-[var(--color-primary-end)]" />
                  <span className="text-sm font-medium text-[var(--color-charcoal)]">{t('results.cards.vertriebsgebiet.overlay.childrenPercentage')}</span>
                </div>

                {/* Progress bar for children */}
                <div className="h-8 rounded-full overflow-hidden bg-[var(--color-charcoal)]/10 mb-3">
                  <div
                    className="h-full rounded-full flex items-center justify-center text-xs font-semibold text-white bg-[var(--color-primary-end)]"
                    style={{ width: `${main.Vertriebsgebiet_Einwohner_Prozent_Kinder}%` }}
                  >
                    {main.Vertriebsgebiet_Einwohner_Prozent_Kinder}%
                  </div>
                </div>

                {/* Additional context */}
                <p className="text-sm text-[var(--color-charcoal)]/70 text-center">
                  {t('results.cards.vertriebsgebiet.overlay.childrenOfPopulation', { percent: main.Vertriebsgebiet_Einwohner_Prozent_Kinder })}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Haushalte & Gebäude section */}
      {(haushaltsverteilungChartConfig || gebaudeartChartConfig ||
        (main.Vertriebsgebiet_Eigentuemer_Prozent !== undefined && main.Vertriebsgebiet_Mieter_Prozent !== undefined) ||
        kinderChartConfig) && (
        <div className="pt-4 border-t border-[var(--glass-border)]">
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-lg font-semibold text-[var(--color-charcoal)]">{t('results.cards.vertriebsgebiet.overlay.householdsAndBuildings')}</h4>
            <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="householdsAndBuildings" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Haushaltsverteilung Donut Chart */}
            {haushaltsverteilungChartConfig && (
              <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
                <div className="flex items-center gap-2 mb-3">
                  <Users2 className="h-5 w-5 text-[var(--color-primary-start)]" />
                  <span className="text-sm font-medium text-[var(--color-charcoal)]">{t('results.cards.vertriebsgebiet.overlay.householdDistribution')}</span>
                </div>
                <AnalysisChart
                  config={haushaltsverteilungChartConfig}
                  height={340}
                />
              </div>
            )}

            {/* Kinder pro Haushalt Bar Chart */}
            {kinderChartConfig && (
              <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
                <div className="flex items-center gap-2 mb-3">
                  <Baby className="h-5 w-5 text-[var(--color-primary-end)]" />
                  <span className="text-sm font-medium text-[var(--color-charcoal)]">{t('results.cards.vertriebsgebiet.overlay.childrenPerHousehold')}</span>
                </div>
                <AnalysisChart
                  config={kinderChartConfig}
                  height={260}
                />
              </div>
            )}

            {/* Eigentümer/Mieter Horizontal Bar */}
            {main.Vertriebsgebiet_Eigentuemer_Prozent !== undefined &&
             main.Vertriebsgebiet_Mieter_Prozent !== undefined && (
              <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)] flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-3">
                  <Key className="h-5 w-5 text-[var(--color-primary-end)]" />
                  <span className="text-sm font-medium text-[var(--color-charcoal)]">{t('results.cards.vertriebsgebiet.overlay.ownerTenant')}</span>
                </div>

                {/* Horizontal stacked bar */}
                <div className="h-10 rounded-full overflow-hidden flex mb-3">
                  <div
                    className="h-full flex items-center justify-center text-xs font-semibold text-white bg-[var(--color-primary-start)]"
                    style={{ width: `${main.Vertriebsgebiet_Eigentuemer_Prozent}%` }}
                  >
                    {main.Vertriebsgebiet_Eigentuemer_Prozent}%
                  </div>
                  <div
                    className="h-full flex items-center justify-center text-xs font-semibold text-white bg-[var(--color-primary-end)]"
                    style={{ width: `${main.Vertriebsgebiet_Mieter_Prozent}%` }}
                  >
                    {main.Vertriebsgebiet_Mieter_Prozent}%
                  </div>
                </div>

                {/* Legend */}
                <div className="flex justify-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[var(--color-primary-start)]" />
                    <span className="text-[var(--color-charcoal)]/70">{t('results.cards.vertriebsgebiet.overlay.owner')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[var(--color-primary-end)]" />
                    <span className="text-[var(--color-charcoal)]/70">{t('results.cards.vertriebsgebiet.overlay.tenant')}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Gebäudeart Bar Chart */}
            {gebaudeartChartConfig && (
              <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
                <div className="flex items-center gap-2 mb-3">
                  <Home className="h-5 w-5 text-[var(--color-primary-start)]" />
                  <span className="text-sm font-medium text-[var(--color-charcoal)]">{t('results.cards.vertriebsgebiet.overlay.buildingType')}</span>
                </div>
                <AnalysisChart
                  config={gebaudeartChartConfig}
                  height={260}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* API Summary section - show if available */}
      {main.Summary && (
        <div className="pt-4 border-t border-[var(--glass-border)]">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-5 w-5 text-[var(--color-primary-start)]" />
            <h4 className="text-lg font-semibold text-[var(--color-charcoal)]">{t('results.cards.vertriebsgebiet.overlay.summaryTitle')}</h4>
            <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="summary" />
          </div>
          <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
            <p className="text-sm text-[var(--color-charcoal)]/80 leading-relaxed">
              {main.Summary}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default VertriebsgebietOverlayContent;
